import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom"; // 👈
import ArrowIcon from "../assets/icons/arrow.svg";
import { useAuth } from "../context/auth-context"; // << CONTEXTO DE AUTENTICAÇÃO

/** --- Resolver de avatar para CRA/Webpack (require.context) --- */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

/** ======= Conectividade: config do ping ======= */
const API_PING_URL = "https://www.google.com.br/"; // ping leve (HEAD)
const API_PING_INTERVAL_MS = 3000;
const API_PING_TIMEOUT_MS = 4000;

/** ======= Cotações: config ======= */
const FX_REFRESH_MS = 5 * 60 * 1000; // 5 min
const FX_TIMEOUT_MS = 7000; // 7s por rodada

// >>> ALTERADO: agora usamos GBP em vez de ARS
type FxTriple = { USD: number | null; EUR: number | null; GBP: number | null };
type FxResult = FxTriple & { at: number | null; from: number | null };

/** Hash simples e estável (string -> 0..5) */
function hashIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 6;
}

/** Tenta resolver ./<id>.jpg/.jpeg/.png; se não achar, usa profile-<1..6>.* */
export function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];
    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1; // 1..6
  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];
  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  for (let i = 1; i <= 6; i++) {
    try {
      return profileCtx(`./profile-${i}.jpg`) as string;
    } catch {}
  }
  return undefined;
}

/** Iniciais como fallback textual (se até a imagem padrão falhar) */
export function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
}

/* =======================================================================================
   Utils gerais
   ======================================================================================= */

function median(nums: number[]): number | null {
  const arr = nums.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!arr.length) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}
function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

/* =======================================================================================
   RateTickerInline — texto puro no fundo (esquerda do Header)
   ======================================================================================= */

// Fonte 1: exchangerate.host (base=BRL) -> invertido p/ BRL por moeda
async function fxFromHost(signal?: AbortSignal): Promise<FxTriple> {
  const url =
    "https://api.exchangerate.host/latest?base=BRL&symbols=USD,EUR,GBP";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const inv = (x: any) => (Number(x) ? 1 / Number(x) : null);
  return {
    USD: inv(j?.rates?.USD),
    EUR: inv(j?.rates?.EUR),
    GBP: inv(j?.rates?.GBP),
  };
}

// Fonte 2: open.er-api.com (base=BRL) -> invertido
async function fxFromER(signal?: AbortSignal): Promise<FxTriple> {
  const url = "https://open.er-api.com/v6/latest/BRL";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const inv = (x: any) => (Number(x) ? 1 / Number(x) : null);
  return {
    USD: inv(j?.rates?.USD),
    EUR: inv(j?.rates?.EUR),
    GBP: inv(j?.rates?.GBP),
  };
}

// Fonte 3: AwesomeAPI — já retorna BRL por moeda (ask/bid)
async function fxFromAwesome(signal?: AbortSignal): Promise<FxTriple> {
  const url = "https://economia.awesomeapi.com.br/last/USD-BRL,EUR-BRL,GBP-BRL";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const take = (k: string) => {
    const v = j?.[k]?.ask ?? j?.[k]?.bid;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    USD: take("USDBRL"),
    EUR: take("EURBRL"),
    GBP: take("GBPBRL"),
  };
}

function mergeFxTriples(list: FxTriple[]): FxTriple {
  const USD = median(
    list.map((x) => x.USD!).filter((v) => v != null) as number[]
  );
  const EUR = median(
    list.map((x) => x.EUR!).filter((v) => v != null) as number[]
  );
  const GBP = median(
    list.map((x) => x.GBP!).filter((v) => v != null) as number[]
  );
  return { USD, EUR, GBP };
}

function fmtBRL(n: number | null) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n!).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

const RateTickerInline: React.FC<{ netOnline: boolean }> = ({ netOnline }) => {
  const [fx, setFx] = useState<FxResult>({
    USD: null,
    EUR: null,
    GBP: null,
    at: null,
    from: null,
  });
  const [loading, setLoading] = useState(true);

  const loadFx = useCallback(async () => {
    if (!netOnline) return;
    setLoading(true);
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), FX_TIMEOUT_MS);
    try {
      const results = await Promise.allSettled([
        fxFromHost(ac.signal),
        fxFromER(ac.signal),
        fxFromAwesome(ac.signal),
      ]);
      const ok: FxTriple[] = [];
      for (const r of results) {
        if (r.status === "fulfilled" && r.value) ok.push(r.value);
      }
      if (!ok.length) return;
      const merged = mergeFxTriples(ok);
      setFx((old) => ({
        USD: merged.USD ?? old.USD,
        EUR: merged.EUR ?? old.EUR,
        GBP: merged.GBP ?? old.GBP,
        from: old.at ?? null,
        at: Date.now(),
      }));
    } catch {
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [netOnline]);

  useEffect(() => {
    loadFx();
    if (!netOnline) return;
    const id = setInterval(loadFx, FX_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadFx, netOnline]);

  const updatedLabel = useMemo(() => {
    if (!fx.at) return "—";
    const d = new Date(fx.at);
    return `Atualizado às ${d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }, [fx.at]);

  const baseText: React.CSSProperties = {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12,
    fontWeight: 300,
    color: "#2b2f33",
    opacity: 0.85,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const sep: React.CSSProperties = { opacity: 0.45, padding: "0 6px" };
  const sym: React.CSSProperties = { opacity: 0.9, paddingRight: 4 };

  const usd = loading ? "—" : fmtBRL(fx.USD);
  const eur = loading ? "—" : fmtBRL(fx.EUR);
  const gbp = loading ? "—" : fmtBRL(fx.GBP);

  return (
    <div
      style={{ marginRight: "auto", paddingLeft: 12, display: "flex" }}
      title={updatedLabel}
      aria-label={`Cotações • ${updatedLabel}`}
    >
      <div style={baseText}>
        <span style={{ fontWeight: 400 }}>USD</span> <span style={sym}>R$</span>
        <span>{usd.replace("R$ ", "").replace("R$", "")}</span>
        <span style={sep}>·</span>
        <span style={{ fontWeight: 400 }}>EUR</span> <span style={sym}>R$</span>
        <span>{eur.replace("R$ ", "").replace("R$", "")}</span>
        <span style={sep}>·</span>
        <span style={{ fontWeight: 400 }}>GBP</span> <span style={sym}>R$</span>
        <span>{gbp.replace("R$ ", "").replace("R$", "")}</span>
      </div>
    </div>
  );
};

/* =======================================================================================
   HRInline — próxima data comemorativa + cor do mês (Brasil)
   ======================================================================================= */

type BRDate = { month: number; day: number; name: string; emoji?: string };

const BR_COMM_DATES: BRDate[] = [
  { month: 1, day: 1, name: "Confraternização Universal 🎉" },
  // (...demais datas...)
];

function getNextBRCommemorative(
  fromDate = new Date()
): { date: Date; label: string } | null {
  const y = fromDate.getFullYear();
  const pool: { d: Date; label: string }[] = [];

  for (const item of BR_COMM_DATES) {
    const dThis = new Date(y, item.month - 1, item.day, 12, 0, 0);
    const dNext = new Date(y + 1, item.month - 1, item.day, 12, 0, 0);

    if (dThis >= startOfDay(fromDate)) {
      pool.push({ d: dThis, label: item.name });
    } else {
      pool.push({ d: dNext, label: item.name });
    }
  }

  pool.sort((a, b) => a.d.getTime() - b.d.getTime());
  return pool.length ? { date: pool[0].d, label: pool[0].label } : null;
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Cores do mês (campanhas de conscientização mais difundidas no BR) */
function getMonthAwarenessColor(monthIndex0: number): {
  name: string;
  hex: string;
} {
  const map: Record<number, { name: string; hex: string }> = {
    0: { name: "Janeiro Branco", hex: "#E5E7EB" },
    1: { name: "Fevereiro Roxo", hex: "#7C3AED" },
    2: { name: "Março Lilás", hex: "#C084FC" },
    3: { name: "Abril Azul", hex: "#2563EB" },
    4: { name: "Maio Amarelo", hex: "#F59E0B" },
    5: { name: "Junho Vermelho", hex: "#DC2626" },
    6: { name: "Julho Amarelo", hex: "#FBBF24" },
    7: { name: "Agosto Dourado", hex: "#F59E0B" },
    8: { name: "Setembro Amarelo", hex: "#FDE047" },
    9: { name: "Outubro Rosa", hex: "#F472B6" },
    10: { name: "Novembro Azul", hex: "#3B82F6" },
    11: { name: "Dezembro Vermelho", hex: "#EF4444" },
  };
  return map[monthIndex0] ?? { name: "—", hex: "#9CA3AF" };
}

const HRInline: React.FC = () => {
  const now = new Date();
  const next = useMemo(() => getNextBRCommemorative(now), [now]);
  const monthInfo = useMemo(
    () => getMonthAwarenessColor(now.getMonth()),
    [now]
  );

  const baseText: React.CSSProperties = {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12,
    fontWeight: 300,
    color: "#2b2f33",
    opacity: 0.9,
    whiteSpace: "nowrap",
    userSelect: "none",
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginRight: "auto",
    paddingLeft: 12,
  };

  const chip: React.CSSProperties = {
    width: 10,
    height: 10,
    borderRadius: 999,
    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
    backgroundColor: monthInfo.hex,
    display: "inline-block",
  };

  const dateText = next
    ? `${pad2(next.date.getDate())}/${pad2(next.date.getMonth() + 1)} — ${
        next.label
      }`
    : "—";

  return (
    <div style={baseText} aria-label="Próxima data comemorativa e cor do mês">
      <span>
        <strong>Próxima data:</strong> {dateText}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={chip} aria-hidden />
        <strong>Cor do mês:</strong> {monthInfo.name}
      </span>
    </div>
  );
};

/* =======================================================================================
   Header
   ======================================================================================= */

const Header: React.FC = () => {
  const { sessao, logout } = useAuth(); // << pega do contexto
  const navigate = useNavigate(); // 👈

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const [liveUrl, setLiveUrl] = useState<string | undefined>(undefined);
  const [companyMenuOpen, setCompanyMenuOpen] = useState<boolean>(false);
  const [changingCompanySlug, setChangingCompanySlug] = useState<string | null>(
    null
  ); // 👈 NOVO

  const menuRef = useRef<HTMLDivElement | null>(null);

  // ===== Conectividade =====
  const [netOnline, setNetOnline] = useState<boolean>(navigator.onLine);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [lastPingAt, setLastPingAt] = useState<number | null>(null);

  const pingApi = useCallback(async () => {
    if (!navigator.onLine) {
      setApiReachable(false);
      setLastPingAt(Date.now());
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), API_PING_TIMEOUT_MS);
    try {
      await fetch(API_PING_URL, {
        method: "HEAD",
        cache: "no-store",
        mode: "no-cors",
        signal: controller.signal,
      });
      setApiReachable(true);
    } catch {
      setApiReachable(false);
    } finally {
      clearTimeout(t);
      setLastPingAt(Date.now());
    }
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setNetOnline(true);
      pingApi();
    };
    const onOffline = () => setNetOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    pingApi();

    const onVis = () => {
      if (document.visibilityState === "visible") pingApi();
    };
    document.addEventListener("visibilitychange", onVis);

    const id = setInterval(pingApi, API_PING_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [pingApi]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  // ==== LOGOUT COMPLETO (Header) ====
  const doLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setMenuOpen(false);
    setCompanyMenuOpen(false);

    try {
      // 1) avisa backend para invalidar sessão/cookie
      try {
        await fetch("https://api.eva-googlemarine.com/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // mesmo se der erro, continua o fluxo local
      }

      // 2) limpa accessToken do localStorage (usado pelas APIs)
      try {
        localStorage.removeItem("access_token");
      } catch {}

      // 3) limpa sessão no contexto React
      await logout();
    } finally {
      setLoggingOut(false);
      // 4) redireciona para a tela de login
      window.location.replace("/");
    }
  };

  // ==== TROCAR EMPRESA (apenas DEV) ====
  const handleSelectCompany = async (slug: string) => {
    if (!slug) return;
    // só faz sentido para desenvolvedor
    const dep = (sessao?.departamento || "").toLowerCase();
    if (dep !== "desenvolvedor") return;

    if (changingCompanySlug) return; // já está processando

    setChangingCompanySlug(slug);

    try {
      // tenta pegar access_token do localStorage (opcional)
      let token = "";
      try {
        if (typeof window !== "undefined") {
          token = localStorage.getItem("access_token") || "";
        }
      } catch {
        token = "";
      }

      const res = await fetch(
        "https://api.eva-googlemarine.com/geral/alterar-empresa",
        {
          method: "POST",
          credentials: "include", // 👈 ENVIA COOKIES
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ slug }), // 👈 slug da empresa selecionada
        }
      );

      if (!res.ok) {
        let extra = "";
        try {
          extra = await res.text();
        } catch {
          /* ignore */
        }
        console.error(
          "Erro ao alterar empresa:",
          res.status,
          res.statusText,
          extra
        );
        window.alert(
          "Não foi possível alterar a empresa. Verifique o slug ou tente novamente."
        );
        return;
      }

      // Opcional: ler resposta, caso queira exibir algo
      // const data = await res.json().catch(() => null);
      // console.log("Empresa alterada:", data);

      // Fecha menus
      setMenuOpen(false);
      setCompanyMenuOpen(false);

      // Força recarregar a aplicação para pegar nova empresa_id na sessão
      window.location.reload();
    } catch (e) {
      console.error("Falha na requisição alterar-empresa:", e);
      window.alert(
        "Falha de rede ao alterar a empresa. Verifique sua conexão e tente novamente."
      );
    } finally {
      setChangingCompanySlug(null);
    }
  };

  const profileUrl = useMemo(() => {
    if (liveUrl) return liveUrl;
    const local = resolveProfileUrl(pictureId, nome);
    return local || undefined;
  }, [liveUrl, pictureId, nome]);

  const hasName = !!nome;

  const netLabel = useMemo(() => {
    if (!netOnline) return "Sem internet";
    if (apiReachable === true) return "Conectado (API ok)";
    if (apiReachable === false) return "Internet ok, API indisponível";
    return "Verificando conexão…";
  }, [netOnline, apiReachable]);

  const netColor = useMemo(() => {
    if (!netOnline) return "#ef4444";
    if (apiReachable === true) return "#10b981";
    if (apiReachable === false) return "#f59e0b";
    return "#9ca3af";
  }, [netOnline, apiReachable]);

  // ===== Lógica de exibição à esquerda (FX x RH) =====
  const dep = (departamento || "").toLowerCase();
  const canShowFX =
    dep === "diretoria" ||
    dep === "comercial" ||
    dep === "coordenacao" ||
    dep === "desenvolvedor";
  const isRH = dep === "recursos-humanos";
  const isDev = dep === "desenvolvedor";

  // 👇 handler para o badge de API Doc
  const handleGoApiDoc = () => {
    navigate("/apidoc");
  };

  return (
    <motion.header
      style={styles.container as any}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <div style={styles.topHairline} />

      {/* ESQUERDA */}
      {isRH ? (
        <HRInline />
      ) : canShowFX ? (
        <RateTickerInline netOnline={netOnline} />
      ) : (
        <div style={{ marginRight: "auto", paddingLeft: 12 }} />
      )}

      {/* DIREITA: badges dev + usuário/menu/conectividade */}
      <div style={styles.userInfo} ref={menuRef}>
        {/* Badges DEV à esquerda do avatar */}
        <AnimatePresence>
          {isDev && (
            <>
              {/* Badge azul de API DOC */}
              <motion.button
                key="dev-api-doc-badge"
                type="button"
                style={styles.devApiBadge as React.CSSProperties}
                onClick={handleGoApiDoc}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <span style={{ fontFamily: "monospace", marginRight: 4 }}>
                  {"</>"}
                </span>
                <span>API DOC</span>
              </motion.button>

              {/* Badge "MODO DESENVOLVEDOR" já existente */}
              <motion.span
                key="dev-badge"
                style={styles.devBadge}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                MODO DESENVOLVEDOR
              </motion.span>
            </>
          )}
        </AnimatePresence>

        <motion.div
          style={{ position: "relative", display: "inline-block" }}
          whileHover={{ y: -1 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
        >
          <AnimatePresence initial={false} mode="wait">
            {profileUrl ? (
              <motion.img
                key="avatar-img"
                src={profileUrl}
                alt={nome ? `Foto de ${nome}` : "Foto do usuário"}
                style={styles.avatar as any}
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMenuOpen((v) => !v);
                  }
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                loading="lazy"
              />
            ) : (
              <motion.div
                key="avatar-fallback"
                style={{ ...styles.avatar, ...styles.avatarFallback }}
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMenuOpen((v) => !v);
                  }
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
              >
                {hasName ? getInitials(nome) : ""}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.span
            key={nome || "nome-loading"}
            style={styles.name}
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.2 }}
          >
            {nome || "—"}
          </motion.span>
        </AnimatePresence>

        <div
          style={{ ...styles.netIndicator, backgroundColor: netColor }}
          title={
            lastPingAt
              ? `${netLabel} • Atualizado há ${Math.max(
                  0,
                  Math.floor((Date.now() - lastPingAt) / 1000)
                )}s`
              : netLabel
          }
          aria-label={netLabel}
        />

        <motion.img
          src={ArrowIcon}
          alt="Abrir menu"
          style={{ ...styles.arrow, cursor: "pointer" }}
          onClick={() => {
            setMenuOpen((v) => {
              const next = !v;
              if (!next) setCompanyMenuOpen(false);
              return next;
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setMenuOpen((v) => {
                const next = !v;
                if (!next) setCompanyMenuOpen(false);
                return next;
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          animate={{ rotate: menuOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
        />

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              {/* ====== Selecionar empresa (apenas DEV) ====== */}
              {isDev && (
                <motion.button
                  type="button"
                  style={styles.menuItem}
                  onClick={() => setCompanyMenuOpen((prev) => !prev)}
                  whileHover={{ backgroundColor: "rgba(18,66,128,.06)" }}
                  whileTap={{ scale: 0.995 }}
                >
                  <span style={{ marginRight: 8 }}>←</span>
                  Selecionar empresa
                </motion.button>
              )}

              <motion.button
                type="button"
                style={styles.menuItem}
                onClick={doLogout}
                disabled={loggingOut}
                whileHover={{ backgroundColor: "rgba(18,66,128,.06)" }}
                whileTap={{ scale: 0.995 }}
              >
                {loggingOut ? "Saindo..." : "Sair"}
              </motion.button>

              {/* ====== Submenu de empresas (abre à esquerda) ====== */}
              {isDev && companyMenuOpen && (
                <motion.div
                  style={styles.companyMenu}
                  initial={{ opacity: 0, x: 6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                >
                  <div style={styles.companyMenuTitle}>Empresas</div>

                  {/* slug: havox */}
                  <button
                    type="button"
                    style={styles.companyMenuItem}
                    onClick={() => handleSelectCompany("havox")}
                    disabled={changingCompanySlug === "havox"}
                  >
                    {changingCompanySlug === "havox"
                      ? "Havox (trocando...)"
                      : "Havox"}
                  </button>

                  {/* slug: googlemarine */}
                  <button
                    type="button"
                    style={styles.companyMenuItem}
                    onClick={() => handleSelectCompany("googlemarine")}
                    disabled={changingCompanySlug === "googlemarine"}
                  >
                    {changingCompanySlug === "googlemarine"
                      ? "Googlemarine (trocando...)"
                      : "Googlemarine"}
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: "fixed",
    top: 0,
    left: "242px",
    height: "54px",
    width: "calc(100% - 222px)",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "50px",
    paddingLeft: "25px",
    boxSizing: "border-box",
    zIndex: 1000,
  },
  topHairline: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background:
      "linear-gradient(90deg, rgba(18,66,128,0.22), rgba(18,66,128,0.02) 60%, rgba(18,66,128,0))",
    pointerEvents: "none",
  },
  userInfo: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    marginLeft: "auto",
  },
  avatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    objectFit: "cover",
    overflow: "hidden",
    display: "inline-block",
    backgroundColor: "#ccd2db",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  avatarFallback: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "26px",
    textAlign: "center",
    userSelect: "none",
    background:
      "linear-gradient(135deg, rgba(18,66,128,0.85), rgba(18,66,128,0.65))",
  },
  devBadge: {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    fontFamily: "'Poppins', sans-serif",
    background:
      "linear-gradient(135deg, rgba(245,158,11,0.16), rgba(245,158,11,0.06))",
    border: "1px solid rgba(245,158,11,0.8)",
    color: "#92400e",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.02)",
  },
  // 👇 badge azul de API DOC
  devApiBadge: {
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    fontFamily: "'Poppins', sans-serif",
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(37,99,235,0.06))",
    border: "1px solid rgba(37,99,235,0.9)",
    color: "#1d4ed8",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.02)",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
  },
  name: {
    marginLeft: "0px",
    marginRight: "9px",
    fontSize: "12px",
    fontWeight: 300,
    fontFamily: "'Poppins', sans-serif",
    color: "#2b2f33",
  },
  netIndicator: {
    width: 8,
    height: 8,
    borderRadius: 999,
    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
  },
  arrow: {
    width: "12px",
    height: "12px",
    filter: "invert(1)",
  },
  menu: {
    position: "absolute",
    top: "36px",
    right: 0,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.04)",
    minWidth: "180px",
    padding: "6px",
    zIndex: 1100,
  },
  menuItem: {
    width: "100%",
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "Arial",
    fontSize: "13px",
    fontWeight: 300,
    color: "#1f2937",
  },
  companyMenu: {
    position: "absolute",
    top: 0,
    right: "100%",
    marginRight: 8,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.04)",
    minWidth: "180px",
    padding: "8px 8px 6px 8px",
    zIndex: 1099,
  },
  companyMenuTitle: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    color: "#374151",
    marginBottom: 6,
    opacity: 0.8,
  },
  companyMenuItem: {
    width: "100%",
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: "8px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "Arial",
    fontSize: "13px",
    fontWeight: 300,
    color: "#1f2937",
  },
};

export default Header;
